<script setup>
import { useI18n } from 'vue-i18n';
import SalesPartyActionsMenu from './SalesPartyActionsMenu.vue';
import SalesPartyQuickAddCard from './SalesPartyQuickAddCard.vue';
import SalesPartyProgressBar from './SalesPartyProgressBar.vue';
import SalesPartyStatusChips from './SalesPartyStatusChips.vue';

defineProps({
  company: { type: Object, required: true },
  progressStatus: { type: Function, required: true },
});

const emit = defineEmits(['add', 'edit', 'remove']);
const { t } = useI18n();
</script>

<template>
  <section class="grid gap-3 rounded-lais-sm bg-lais-bg-soft p-4">
    <h3 class="text-sm font-semibold text-lais-text">
      {{ t('SALES_PARTIES.COMMON.REPRESENTATIVES') }}
    </h3>
    <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      <button
        v-if="!company.representatives?.length"
        type="button"
        class="flex min-h-[14.5rem] flex-col items-center justify-between gap-4 rounded-lais border border-lais-border-soft bg-white p-5 text-center transition hover:border-lais-primary/50 focus-visible:outline-none focus-visible:shadow-lais-focus"
        data-test="pending-representative"
        @click="emit('add')"
      >
        <span class="flex w-full flex-wrap gap-2">
          <span
            class="rounded-full bg-amber-100 px-2 py-1 text-[11px] font-semibold uppercase text-amber-700"
          >
            {{ t('SALES_PARTIES.LIST.STATUS_PENDING') }}
          </span>
          <span
            class="rounded-full bg-lais-bg-soft px-2 py-1 text-[11px] font-semibold uppercase text-lais-text-muted"
          >
            {{ t('SALES_PARTIES.LIST.COMPANY_TYPE') }}
          </span>
        </span>
        <span
          class="i-lucide-user-round size-[60px] text-lais-primary"
          aria-hidden="true"
        />
        <span class="text-sm text-lais-text-muted">
          {{ t('SALES_PARTIES.LIST.PENDING_REPRESENTATIVE') }}
        </span>
      </button>
      <article
        v-for="representative in company.representatives"
        :key="representative.id"
        class="flex min-h-[14.5rem] flex-col justify-between gap-4 rounded-lais border border-lais-border-soft bg-white p-5 text-left transition hover:border-lais-primary/50 focus-visible:outline-none focus-visible:shadow-lais-focus"
        data-test="representative-card"
      >
        <div class="flex items-start justify-between gap-3">
          <SalesPartyStatusChips
            :percent="Number(representative.progress?.percent || 0)"
            :status="progressStatus(representative)"
            :type-label="t('SALES_PARTIES.LIST.LEGAL_BADGE')"
          />
          <SalesPartyActionsMenu
            @edit="emit('edit', representative)"
            @remove="emit('remove', representative)"
          />
        </div>
        <button
          type="button"
          class="grid w-full gap-3 text-left"
          data-test="representative-edit"
          @click="emit('edit', representative)"
        >
          <span
            class="size-[60px] text-lais-primary"
            :class="
              Number(representative.progress?.percent) === 100
                ? 'i-lucide-check'
                : 'i-lucide-user-round'
            "
            aria-hidden="true"
          />
          <span class="min-w-0">
            <span class="block truncate text-sm font-semibold text-lais-text">
              {{ representative.display_name }}
            </span>
          </span>
          <span class="text-xs font-bold text-lais-primary">
            {{
              t('SALES_PARTIES.COMMON.PERCENT', {
                value: representative.progress?.percent || 0,
              })
            }}
          </span>
          <SalesPartyProgressBar
            :label="representative.display_name"
            :value="Number(representative.progress?.percent || 0)"
          />
        </button>
      </article>
      <SalesPartyQuickAddCard
        :label="t('SALES_PARTIES.ACTIONS.ADD_REPRESENTATIVE')"
        @select="emit('add')"
      />
    </div>
  </section>
</template>
